import { Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { CheckCircle, Info, Pause, Play, Triangle, X } from '@phosphor-icons/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNodes, type Node } from 'reactflow';
import dayjs from 'dayjs';
import styles from './AgentStateVisual.module.css';
import { formatTime, formatTokens } from './helpers';

const STEP_FAKE_DURATION_MS = 25000; // 25 seconds
const STEP_FAKE_MIN_OFFSET = 1; // e.g. 25% -> start at 26%
const STEP_FAKE_MAX_OFFSET = 3; // e.g. 50% -> cap at 47% until completion

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function useTimer(interval = 10) {
  const timeRef = useRef(0);
  const intervalRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const isRunningRef = useRef(false);

  const start = () => {
    if (isRunningRef.current) return;
    isRunningRef.current = true;

    intervalRef.current = setInterval(() => {
      timeRef.current += interval;
    }, interval);
  };

  const stop = () => {
    isRunningRef.current = false;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
  };

  const reset = () => {
    timeRef.current = 0;
  };

  const getTime = () => timeRef.current;

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return { start, stop, reset, getTime };
}

const AgentStateVisual = () => {
  const nodes = useNodes();
  const timer = useTimer(10);

  const [lastCompletionTime, setLastCompletionTime] = useState(0);
  const [accumulatedTokens, setAccumulatedTokens] = useState(0);
  const [hasFinishedState, setHasFinishedState] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [fakeProgressPercentage, setFakeProgressPercentage] = useState<number | null>(null);

  const isTimerRunningRef = useRef(false);
  const timeTextRef = useRef<HTMLParagraphElement>(null);
  const processedNodesRef = useRef<Set<string>>(new Set());
  const stepStartedAtRef = useRef<number>(0);

  const agentGlobalNode = useMemo(
    () => nodes.find(node => node.type === 'globalStarter') as Node,
    [nodes]
  );

  const isGlobalLoading = agentGlobalNode?.data?.loading;
  const hasFinished = agentGlobalNode?.data?.hasFinished;
  const isRunning = isGlobalLoading || nodes.some((node: Node) => node?.data?.flowTrigger?.id);
  const isPaused = agentGlobalNode?.data?.paused;

  useEffect(() => {
    if (isRunning && !hasFinished) {
      setHasFinishedState(false);
      setIsClosing(false);
      setAccumulatedTokens(0);
      processedNodesRef.current.clear();
    } else if (hasFinished) {
      setHasFinishedState(true);
    }
  }, [hasFinished, isRunning]);

  useEffect(() => {
    if (!isRunning) return;

    nodes.forEach((node: Node) => {
      const usedTokens = (node.data as any)?.usedTokens || 0;

      if (usedTokens > 0 && !processedNodesRef.current.has(node.id)) {
        setAccumulatedTokens(prev => prev + usedTokens);
        processedNodesRef.current.add(node.id);
      }
    });
  }, [nodes, isRunning]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setHasFinishedState(false);
      setIsClosing(false);
    }, 300);
  };

  const activeNodeData = useMemo(() => {
    const activeNode = nodes.find((n: Node) => n?.data?.flowTrigger?.id);
    if (!activeNode) return null;

    const data = activeNode.data as any;
    const trigger = data?.flowTrigger;

    return {
      name: data?.label || activeNode.type || 'Agent is running',
      currentStep: trigger?.completed ? trigger.completed + 1 : 1,
      totalSteps: trigger?.allIds?.length || 0
    };
  }, [nodes]);

  const hasError = useMemo(
    () => nodes.some((node: Node) => node?.data?.hasExecutionError === true),
    [nodes]
  );

  const lastRunDate = useMemo(
    () => agentGlobalNode?.data?.logs?.[0]?.date || null,
    [agentGlobalNode]
  );

  const progressPercentage = useMemo(() => {
    if (hasFinishedState) return 100;
    if (!activeNodeData || activeNodeData.totalSteps === 0) return 0;
    return Math.round(((activeNodeData.currentStep - 1) / activeNodeData.totalSteps) * 100);
  }, [hasFinishedState, activeNodeData]);

  const stepKey = useMemo(() => {
    if (!activeNodeData || activeNodeData.totalSteps === 0) return null;
    return `${activeNodeData.currentStep}/${activeNodeData.totalSteps}`;
  }, [activeNodeData]);

  const stepBounds = useMemo(() => {
    if (!activeNodeData || activeNodeData.totalSteps === 0) return null;

    const total = activeNodeData.totalSteps;
    const current = activeNodeData.currentStep;

    const stepStart = ((current - 1) / total) * 100;
    const stepEnd = (current / total) * 100;

    const fakeStart = clamp(stepStart + STEP_FAKE_MIN_OFFSET, stepStart, stepEnd);
    const fakeEnd = clamp(stepEnd - STEP_FAKE_MAX_OFFSET, fakeStart, stepEnd);

    return {
      stepStartRounded: Math.round(stepStart),
      stepEndRounded: Math.round(stepEnd),
      fakeStart,
      fakeEnd,
    };
  }, [activeNodeData]);

  useEffect(() => {
    if (!isRunning || isPaused || hasFinishedState || !stepBounds || !stepKey) {
      setFakeProgressPercentage(null);
      return;
    }

    stepStartedAtRef.current = Date.now();
    setFakeProgressPercentage(Math.floor(stepBounds.fakeStart));
  }, [isRunning, isPaused, hasFinishedState, stepKey, stepBounds]);

  useEffect(() => {
    if (!isRunning || isPaused || hasFinishedState || !stepBounds || !stepKey) return;

    const id = setInterval(() => {
      const elapsed = Date.now() - stepStartedAtRef.current;
      const t = Math.min(1, elapsed / STEP_FAKE_DURATION_MS);

      const raw = stepBounds.fakeStart + (stepBounds.fakeEnd - stepBounds.fakeStart) * t;
      const next = Math.floor(raw);

      setFakeProgressPercentage(prev => {
        const prevVal = prev ?? stepBounds.stepStartRounded;
        return Math.max(prevVal, next);
      });
    }, 200);

    return () => clearInterval(id);
  }, [isRunning, isPaused, hasFinishedState, stepKey, stepBounds]);

  const displayProgressPercentage = useMemo(() => {
    if (hasFinishedState) return 100;
    if (!stepBounds || !activeNodeData || activeNodeData.totalSteps === 0) return 0;

    const base = progressPercentage;
    const stepEnd = stepBounds.stepEndRounded;

    const fake = fakeProgressPercentage ?? base;
    return clamp(Math.max(base, fake), 0, stepEnd);
  }, [hasFinishedState, stepBounds, activeNodeData, progressPercentage, fakeProgressPercentage]);

  useEffect(() => {
    const isActive = isRunning && !isPaused;

    if (isActive && !isTimerRunningRef.current) {
      timer.reset();
      timer.start();
      isTimerRunningRef.current = true;
    } else if (hasFinishedState && isTimerRunningRef.current) {
      timer.stop();
      setLastCompletionTime(timer.getTime());
      isTimerRunningRef.current = false;
    } else if (!isRunning && !hasFinishedState && isTimerRunningRef.current) {
      timer.stop();
      timer.reset();
      isTimerRunningRef.current = false;
    }
  }, [isRunning, isPaused, hasFinishedState, timer]);

  useEffect(() => {
    if (!isRunning || isPaused) return;

    const displayInterval = setInterval(() => {
      if (timeTextRef.current) {
        timeTextRef.current.textContent = `Time Lapsed: ${formatTime(timer.getTime())}`;
      }
    }, 100);

    return () => clearInterval(displayInterval);
  }, [isRunning, isPaused, timer]);

  const statusType = !hasFinishedState ? 'running' : hasError ? 'error' : 'success';

  const STATUS_STYLES = {
    running: {
      color: '#3B82F6',
      colorClass: styles.colorRunning,
      bgClass: styles.bgRunning,
      gradientClass: styles.gradientRunning,
    },
    success: {
      color: '#059669',
      colorClass: styles.colorSuccess,
      bgClass: styles.bgSuccess,
      gradientClass: styles.gradientSuccess,
    },
    error: {
      color: '#DC2626',
      colorClass: styles.colorError,
      bgClass: styles.bgError,
      gradientClass: styles.gradientError,
    },
  };

  const { color, colorClass, bgClass, gradientClass } = STATUS_STYLES[statusType];

  const statusText = hasFinishedState
    ? 'Agent is finished!'
    : `${activeNodeData?.name || 'Agent'} is running`;

  const statusKey = hasFinishedState
    ? 'finished'
    : isGlobalLoading
      ? 'starting'
      : `running-${activeNodeData?.name || 'default'}`;

  const renderIcon = () => {
    if (hasFinishedState) {
      return hasError
        ? <Triangle size={20} color={color} />
        : <CheckCircle size={20} color={color} />;
    }
    return isPaused
      ? <Pause size={20} weight="fill" color={color} />
      : <Play size={20} weight="fill" color={color} />;
  };

  if (!isRunning && !hasFinishedState) return null;

  return (
    <div className={styles.containerWrapper}>
      <div className={`${styles.container} ${isClosing ? styles.closing : ''}`}>
        <div className={styles.wrapper}>
          <div
            className={`${styles.gradientOverlay} ${gradientClass}`}
            style={{ width: `${displayProgressPercentage}%` }}
          />

          <div className={styles.content}>
            <div className={styles.leftSection}>
              <div className={styles.iconWrapper}>
                <div key={statusKey} className={styles.iconContainer}>
                  {renderIcon()}
                </div>
              </div>

              <div className={styles.textWrapper}>
                <div key={statusKey} className={styles.textContainer}>
                  <p className={`${styles.statusText} ${colorClass}`}>{statusText}</p>
                  {isRunning && activeNodeData && activeNodeData.totalSteps > 0 && (
                    <Chip
                      color='primary'
                      label={`Step ${activeNodeData.currentStep} of ${activeNodeData.totalSteps}`}
                      size="small"
                    />
                  )}
                </div>
              </div>
            </div>

            <div className={styles.rightSection}>
              {isRunning && (
                <p ref={timeTextRef} className={styles.timeText}>
                  Time Lapsed: 00:00:00
                </p>
              )}

              {hasFinishedState && lastRunDate && (
                <Tooltip
                  title={
                    <div className={styles.tooltipContent}>
                      <Typography className={styles.tooltipText}>
                        <b>Time to finish:</b> {formatTime(lastCompletionTime)}
                      </Typography>
                      <Typography className={styles.tooltipText}>
                        <b>Last run:</b> {dayjs(lastRunDate).format('MMM D, YYYY h:mm A')}
                      </Typography>
                      <Typography className={styles.tooltipText}>
                        <b>Tokens used:</b> {formatTokens(accumulatedTokens)}
                      </Typography>
                    </div>
                  }
                  arrow
                >
                  <div className={styles.infoIcon}>
                    <Info size={16} weight="fill" color={color} />
                  </div>
                </Tooltip>
              )}

              {isRunning && <p className={styles.percentageText}>{displayProgressPercentage}%</p>}
            </div>
          </div>

          <div className={styles.progressBar}>
            <div
              className={`${styles.progressFill} ${bgClass}`}
              style={{ width: `${displayProgressPercentage}%` }}
            />
          </div>
          {hasFinishedState && (
            <IconButton sx={{ marginRight: '12px' }} onClick={handleClose}>
              <X size={16} color={color} />
            </IconButton>
          )}
        </div>

      </div>
    </div>
  );
};

export default AgentStateVisual;

